/*
typical usage:
--------------------------------

const { carouselRef, current, setCurrent } = useCarousel({
  snapElement: ".carousel-snap",
  spacerElement: ".carousel-spacer",
  setOuterHeight: true,
})

return (
  <div ref={carouselRef} className="carousel relative">
    <div className="carousel-spacer w-4" />
    {images.map((image, index) => (
      <div key={index} className="carousel-snap">
        <ResponsiveImage {...image} />
      </div>
    ))}
  </div>
)
*/

import { useEffect, useRef, useState } from "react"
import { useRafLoop, useWindowSize } from "react-use"
import { ResponsiveImageFragment } from "../types.graphql"

export function useCarousel({
  snapElement,
  spacerElement,
  targetSizeElement,
  secondarySizeElement,
  imagesData,
  setOuterHeight = false,
  startEnabled = true,
  startControls = true,
  slide = true,
}: {
  snapElement: string
  spacerElement?: string
  targetSizeElement?: string
  secondarySizeElement?: string
  imagesData?: Array<ResponsiveImageFragment | null>
  setOuterHeight?: boolean
  startEnabled?: boolean
  startControls?: boolean
  slide?: boolean
}) {
  const ref = useRef<HTMLDivElement>(null)
  const { width } = useWindowSize()
  const lastTick = useRef(0)
  const [current, setCurrent] = useState(0)
  const [enabled, setEnabled] = useState(startEnabled)
  const [controls, setControls] = useState(startControls)
  const index = useRef(0)
  const primarySize = useRef<HTMLDivElement | null>(null)
  const secondarySize = useRef<HTMLDivElement | null>(null)
  const elements = useRef<NodeListOf<HTMLElement> | null>(null)
  const elementPositions = useRef(
    [] as {
      x: number
      w: number
      h: number
      xLerp: number
      wLerp: number
      hLerp: number
      image?: ResponsiveImageFragment | null
    }[],
  )
  const spacer = useRef<HTMLDivElement | null>(null)
  const drag = useRef({
    down: false,
    dragged: false,
    start: {
      x: 0,
      y: 0,
    },
    delta: {
      x: 0,
      y: 0,
    },
    current: {
      xLerp: 0,
      yLerp: 0,
    },
  })

  const next = () => {
    const total = elements.current?.length || 1
    index.current = index.current + 1 < total ? index.current + 1 : 0
    setCurrent(index.current)
  }

  const previous = () => {
    const total = elements.current?.length || 1
    index.current = index.current - 1 >= 0 ? index.current - 1 : total - 1
    setCurrent(index.current)
  }

  const pointerDown = (e: PointerEvent) => {
    if (!controls) return
    if (e.target instanceof HTMLElement && ref.current?.contains(e.target)) {
      const d = drag.current
      d.start.x = e.clientX
      d.start.y = e.clientY
      d.down = true
      d.dragged = false
    }
  }

  const pointerMove = (e: PointerEvent) => {
    if (!controls) return
    const d = drag.current
    if (!d.down) return
    d.delta.x = e.clientX - d.start.x
    d.delta.y = e.clientY - d.start.y

    if (Math.abs(d.delta.x) > 10) {
      d.dragged = true
    }

    // snap
    if (Math.abs(d.delta.x) > (elements.current?.[0].offsetWidth || window.innerWidth) * 0.15) {
      if (d.delta.x < 0) next()
      else previous()
      pointerUp()
    }
  }

  const pointerUp = () => {
    if (!controls) return
    const d = drag.current
    if (!d.down) return
    d.down = false
    d.delta.x = 0
    d.delta.y = 0
  }

  const preventDefault = (e: Event) => {
    if (!controls) return
    e.preventDefault()
  }

  const canClick = (e: Event) => {
    if (!controls) return
    if (drag.current.dragged) {
      e.preventDefault()
      e.stopPropagation()
    }
  }

  const onTouch = (e: TouchEvent) => {
    if (!controls) return
    if (drag.current.dragged && drag.current.down) {
      e.preventDefault()
      e.stopPropagation()
    }
  }

  useEffect(() => {
    elements.current = ref.current?.querySelectorAll(snapElement) || null
    spacer.current = (spacerElement ? ref.current?.querySelector(spacerElement) : null) || null
    primarySize.current = (targetSizeElement ? ref.current?.querySelector(targetSizeElement) : null) || null
    secondarySize.current = (secondarySizeElement ? ref.current?.querySelector(secondarySizeElement) : null) || null
    const links = ref.current?.querySelectorAll("a")

    if (elements.current) {
      elements.current.forEach((el, i) => {
        if (enabled) {
          el.style.setProperty("position", "absolute")
          el.style.setProperty("left", "0px")
          el.style.setProperty("top", "0px")
          el.style.setProperty("transition", "none")
          el.style.setProperty("user-select", "none")
        } else {
          el.style.removeProperty("position")
          el.style.removeProperty("left")
          el.style.removeProperty("top")
          el.style.removeProperty("transition")
          el.style.removeProperty("user-select")
        }

        const image = el.querySelector("img") as HTMLImageElement
        if (image) {
          if (enabled) {
            image.style.setProperty("transition", "none")
          } else {
            image.style.removeProperty("transition")
          }
        }

        elementPositions.current[i] = {
          x: 0,
          w: 0,
          h: 0,
          xLerp: 0,
          wLerp: 0,
          hLerp: 0,
          image: imagesData?.[i],
        }
      })
    }

    if (links) {
      links.forEach((el) => {
        el.addEventListener("mousedown", preventDefault, false)
        el.addEventListener("mousemove", preventDefault, false)
        el.addEventListener("click", canClick, false)
      })
    }

    return () => {
      if (links) {
        links.forEach((el) => {
          el.removeEventListener("mousedown", preventDefault, false)
          el.removeEventListener("mousemove", preventDefault, false)
          el.removeEventListener("click", canClick, false)
        })
      }
    }
  }, [width, ref, enabled])

  useRafLoop(() => {
    if (Date.now() - lastTick.current < 1000 / 60) return

    drag.current.current.xLerp += (drag.current.delta.x - drag.current.current.xLerp) * 0.2
    drag.current.current.yLerp += (drag.current.delta.y - drag.current.current.yLerp) * 0.2

    if (elements.current && ref.current) {
      let x = 0
      let currentX = 0
      let maxH = 0
      const space = spacer.current?.offsetWidth || 0
      const primaryW = primarySize.current?.offsetWidth || 0
      const primaryH = primarySize.current?.offsetHeight || 0
      const secondaryW = secondarySize.current?.offsetWidth || primaryW
      const secondaryH = secondarySize.current?.offsetHeight || primaryH

      elements.current.forEach((el, i) => {
        const pos = elementPositions.current[i]
        const w = el.offsetWidth
        const h = el.offsetHeight
        const ratio =
          (pos.image?.mediaDetails?.width || ref.current!.offsetWidth) /
          (pos.image?.mediaDetails?.height || ref.current!.offsetHeight)

        pos.x = x
        if (primarySize.current) {
          pos.h = i === current ? primaryH : secondaryH
          pos.w = pos.h * ratio
        } else {
          pos.w = w
          pos.h = h
        }

        if (i === index.current) {
          currentX = x
        }

        x += pos.w + space
        maxH = Math.max(maxH, pos.h)
      })

      elements.current.forEach((el, i) => {
        const pos = elementPositions.current[i]

        pos.wLerp += (pos.w - pos.wLerp) * 0.125
        pos.hLerp += (pos.h - pos.hLerp) * 0.125
        pos.xLerp += (pos.x - currentX + drag.current.current.xLerp - pos.xLerp) * 0.125

        pos.wLerp = Math.round(pos.wLerp * 1000) / 1000
        pos.hLerp = Math.round(pos.hLerp * 1000) / 1000
        pos.xLerp = Math.round(pos.xLerp * 1000) / 1000

        if (enabled) {
          if (primarySize.current) el.style.width = `${pos.wLerp}px`
          if (primarySize.current) el.style.height = `${pos.hLerp}px`
          if (slide) {
            el.style.transform = `translateX(${pos.xLerp}px)`
          }
        } else {
          el.style.removeProperty("width")
          el.style.removeProperty("height")
          el.style.removeProperty("transform")
        }
      })

      if (setOuterHeight) {
        if (enabled) {
          ref.current.style.height = `${maxH}px`
        } else {
          ref.current.style.removeProperty("height")
        }
      }
    }
  })

  useEffect(() => {
    index.current = current
  }, [current])

  useEffect(() => {
    window.addEventListener("pointerdown", pointerDown, { passive: true })
    window.addEventListener("pointermove", pointerMove, { passive: true })
    window.addEventListener("pointerup", pointerUp, { passive: true })
    window.addEventListener("pointercancel", pointerUp, { passive: true })
    window.addEventListener("pointerleave", pointerUp, { passive: true })
    ref.current?.addEventListener("touchmove", onTouch, false)

    return () => {
      window.removeEventListener("pointerdown", pointerDown)
      window.removeEventListener("pointermove", pointerMove)
      window.removeEventListener("pointerup", pointerUp)
      window.removeEventListener("pointercancel", pointerUp)
      window.removeEventListener("pointerleave", pointerUp)
      ref.current?.removeEventListener("touchmove", onTouch)
    }
  }, [])

  return {
    carouselRef: ref,
    current,
    setCurrent,
    setEnabled,
    setControls,
  }
}
